import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import { appApi, inr, fmtDate } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";

export default function Merchants() {
  const [users, setUsers] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    appApi.get("admin/users").then((d) => setUsers(d.users || [])).catch(() => setUsers([]));
  }, []);

  const filtered = (users || []).filter((u) =>
    [u.name, u.email, u.business].some((v) => (v || "").toLowerCase().includes(q.toLowerCase()))
  );

  return (
    <div data-testid="merchants-page" className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs font-mono font-bold uppercase tracking-[0.2em] text-blaze">Admin</p>
          <h1 className="font-display font-black tracking-tight text-3xl sm:text-4xl mt-1">Merchants</h1>
        </div>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, business"
            data-testid="merchants-search-input"
            className="border-2 border-obsidian bg-white pl-9 pr-4 py-2.5 text-sm w-72 outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obsidian"
          />
        </div>
      </div>

      <div className="border-2 border-obsidian bg-white overflow-x-auto">
        <table className="w-full text-sm min-w-[860px]" data-testid="merchants-table">
          <thead>
            <tr className="border-b-2 border-obsidian text-left">
              {["Merchant", "Email", "KYC", "Wallet", "Orders", "GMV", "Joined"].map((h) => (
                <th key={h} className="px-4 py-3 text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((u) => (
              <tr key={u.id} className="border-b border-zinc-100 last:border-0 hover:bg-acid/20 transition-colors duration-100" data-testid={`merchant-row-${u.id}`}>
                <td className="px-4 py-3">
                  <Link to={`/admin/merchants/${u.id}`} data-testid={`merchant-link-${u.id}`} className="font-bold hover:text-blaze transition-colors duration-100">
                    {u.name || "—"}
                  </Link>
                  <p className="text-xs text-zinc-500">{u.business || ""}</p>
                </td>
                <td className="px-4 py-3 font-mono text-xs text-zinc-600">{u.email}</td>
                <td className="px-4 py-3"><StatusBadge status={u.kyc_status || "pending"} /></td>
                <td className="px-4 py-3 font-mono text-xs">{inr(u.wallet_inr)}</td>
                <td className="px-4 py-3 font-mono text-xs">{u.orders_count ?? 0}</td>
                <td className="px-4 py-3 font-mono text-xs font-bold">{inr(u.gmv_inr)}</td>
                <td className="px-4 py-3 font-mono text-xs text-zinc-500">{fmtDate(u.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {users && filtered.length === 0 && <p className="px-5 py-10 text-sm text-zinc-500" data-testid="merchants-empty">No merchants match that search.</p>}
        {!users && <p className="px-5 py-10 font-mono text-xs uppercase tracking-[0.3em] text-zinc-400">Loading merchants…</p>}
      </div>
      <p className="text-xs font-mono text-zinc-400 uppercase tracking-widest" data-testid="merchants-count">
        {users ? `${filtered.length} of ${users.length} merchants` : ""}
      </p>
    </div>
  );
}
